/**
 * Synthetic farm renewal offers (#31411, #35226).
 *
 * tasks-be writes one offer per farm renewal purely so docs-engine can render the renewal
 * declaration (`tasks-be/lambdas/renewals/shared/farm/build-renewal-offer.ts`). It is not a
 * quote: it starts from an empty quote and carries only the fields the dec pipeline reads, so
 * `quote.offerings` is never set and the staff offer page cannot render it.
 *
 * The id is deterministic — `<renewalPolicyId>-renewal-offer` — so regenerating a renewal
 * overwrites its offer instead of orphaning a new one. That makes the id the only marker these
 * records carry, and the suffix lives here rather than at each call site so the code that MINTS
 * the id and the code that has to RECOGNISE it (search indexing, the staff route guard) cannot
 * drift apart.
 */

export const SYNTHETIC_RENEWAL_OFFER_SUFFIX = '-renewal-offer';

/** The offer id tasks-be writes for a farm renewal policy. */
export const syntheticRenewalOfferId = (renewalPolicyId: string): string =>
  `${renewalPolicyId}${SYNTHETIC_RENEWAL_OFFER_SUFFIX}`;

/** True for an offer id minted by `syntheticRenewalOfferId` — i.e. a record no human should open. */
export const isSyntheticRenewalOfferId = (offerId: string | null | undefined): boolean =>
  typeof offerId === 'string' &&
  offerId.length > SYNTHETIC_RENEWAL_OFFER_SUFFIX.length &&
  offerId.endsWith(SYNTHETIC_RENEWAL_OFFER_SUFFIX);

/**
 * The renewal policy a synthetic offer was reconstructed from, or `null` if the id is not one.
 * Staff who land on such an offer want the policy, and it is recoverable from the id alone — no
 * lookup needed.
 */
export const policyIdFromSyntheticRenewalOfferId = (offerId: string | null | undefined): string | null =>
  isSyntheticRenewalOfferId(offerId) ? (offerId as string).slice(0, -SYNTHETIC_RENEWAL_OFFER_SUFFIX.length) : null;
